import { List, Map } from "immutable";
import moment from "moment";
import { createSelector } from "reselect";

import {
  composeSelector,
  composeSelectors
} from "seneca-common/utils/selectors/compose-selectors";

import { PATH_TO_TEACHER_ASSIGNMENTS_STATE } from "../consts";
import TeacherAssignment from "../models/TeacherAssignment";
import TeacherAssignmentsState from "../models/TeacherAssignmentsState";
import { getAssignments } from "./state";
import * as teacherAssignments from "./state/assignments/teacherAssignments";

type State = any;
type AssignmentId = string;
type CourseId = string;
type SectionId = string;
type QuestionId = string;
type UserId = string;

export function getTeacherAssignmentsState(
  state: State
): TeacherAssignmentsState {
  return state.get(PATH_TO_TEACHER_ASSIGNMENTS_STATE);
}

type GetStateAssignments = (
  arg0: State
) => Map<AssignmentId, TeacherAssignment>;
export const getStateAssignments: GetStateAssignments = composeSelector(
  getTeacherAssignmentsState,
  getAssignments
);

// @ts-ignore
export const getAssignment: (
  State: any,
  AssignmentId: any
) => TeacherAssignment = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignment
);

export const getAssignmentSectionLastSetDate: (
  State: any,
  SectionId: any
) => string | undefined = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentSectionLastSetDate
);

export const getAssignmentStartDate: (State: any, AssignmentId: any) => string =
  composeSelector(
    getStateAssignments as any,
    teacherAssignments.getAssignmentStartDate
  );

export const getAssignmentDueDate: (State: any, AssignmentId: any) => string =
  composeSelector(
    getStateAssignments as any,
    teacherAssignments.getAssignmentDueDate
  );

export const getAssignmentName: (State: any, AssignmentId: any) => string =
  composeSelector(
    getStateAssignments as any,
    teacherAssignments.getAssignmentName
  );

export const getAssignmentNumberOfAssignees: (
  State: any,
  AssignmentId: any
) => number = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentNumOfAssignees
);

export const getAssignmentCourseId: (
  State: any,
  AssignmentId: any
) => CourseId = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentCourseId
);

export const getAssignmentSectionIds: (
  State: any,
  AssignmentId: any
) => List<SectionId> = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentSectionIds
);

export const getAssignmentExamQuestionIds: (
  State: any,
  AssignmentId: any
) => List<QuestionId> = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentExamQuestionIds
);

export const getAssignmentNumberOfExamQuestions: (
  State: any,
  AssignmentId: any
) => number = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentNumberOfExamQuestions
);

export const getAssignmentAssigneesWithSections: (
  State: any,
  AssignmentId: string
) => Map<UserId, List<SectionId>> = composeSelector(
  getStateAssignments,
  teacherAssignments.getAssigneesWithSections
);

export const getAssignmentNumberOfSections: (
  State: any,
  AssignmentId: any
) => number = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentNumberOfSections
);

export const getAssignmentAssignees: (
  State: any,
  AssignmentId: any
) => List<UserId> = composeSelector(
  getStateAssignments,
  teacherAssignments.getAssignmentAssignees
);

export const getIsPastAssignment: (
  State: any,
  AssignmentId: string,
  moment: any
) => boolean = (state: any, assignmentId, now) => {
  const assignmentsState = getStateAssignments(state);
  return teacherAssignments.getIsPastAssignment(
    assignmentsState,
    assignmentId,
    now
  );
};

export const getIsAssignmentAfterCutoffDate: (
  arg0: State,
  arg1: AssignmentId,
  arg2: moment.Moment
) => boolean = (state: any, assignmentId, now) => {
  const assignmentsState = getStateAssignments(state);
  return teacherAssignments.getIsAssignmentAfterCutoffDate(
    assignmentsState,
    assignmentId,
    now
  );
};

export const getHasNoAssignees: (State: any, AssignmentId: any) => boolean =
  createSelector(getAssignmentNumberOfAssignees, number => number === 0);

export const makeGetClassAssignmentIds = (classId: AssignmentId) => {
  return createSelector(getStateAssignments, assignments =>
    teacherAssignments.getClassAssignmentIds(assignments as any, classId)
  );
};

type MakeGetNumberOfClassAssignmentsReturn = (arg0: State) => number;
export const makeGetNumberOfClassAssignments = (
  classId: AssignmentId
): MakeGetNumberOfClassAssignmentsReturn => {
  return createSelector(getStateAssignments, assignments =>
    teacherAssignments.getNumberOfClassAssignments(assignments as any, classId)
  );
};

export const assignmentExists: (State: any, AssignmentId: any) => boolean =
  composeSelector(getStateAssignments, teacherAssignments.assignmentExists);

export const getAssignmentSpecType: (State: any, AssignmentId: any) => string =
  composeSelector(
    getStateAssignments as any,
    teacherAssignments.getAssignmentSpecType
  );

export const getAssignmentIsSmart: (State: any, AssignmentId: any) => boolean =
  composeSelector(
    getStateAssignments as any,
    teacherAssignments.getAssignmentIsSmart
  );

export const getAutoEnrolNewStudents: (
  State: any,
  AssignmentId: any
) => boolean = composeSelector(
  getStateAssignments,
  teacherAssignments.getAutoEnrolNewStudents
);

// @ts-ignore
export const getAssignmentCutoffDate: (
  State: any,
  AssignmentId: any
) => boolean = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentCutoffDate
);

export const makeGetNumberOfUpcomingClassAssignments: (
  arg0: AssignmentId,
  arg1: moment.Moment
) => (state: State) => number = (assignmentId, now) =>
  createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNumberOfUpcomingClassAssignments(
      assignmentsState as any,
      assignmentId,
      now
    )
  );

export const assignmentIncludesExamQuestions: (
  state: any,
  assignmentId: string
) => boolean = composeSelector(
  getStateAssignments as any,
  teacherAssignments.assignmentIncludesExamQuestions
);

export const assignmentHasNoSections: (
  state: any,
  assignmentId: string
) => boolean = composeSelector(
  getStateAssignments as any,
  teacherAssignments.assignmentHasNoSections
);

export const makeGetNumberOfUpcomingClassAssignmentsWithExamQuestions = (
  classId: string,
  now: moment.Moment
) => {
  return createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNumberOfUpcomingClassAssignmentsWithExamQuestions(
      assignmentsState as any,
      classId,
      now
    )
  );
};

export const makeGetNextDueAssignmentWithExamQuestions = (
  classId: string,
  now: moment.Moment
) => {
  return createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNextDueAssignmentWithExamQuestions(
      assignmentsState as any,
      classId,
      now
    )
  );
};

export const makeGetNumberOfPastClassAssignments: (
  AssignmentId: string,
  moment: any
) => (state: State) => number = (assignmentId, now) =>
  createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNumberOfPastClassAssignments(
      assignmentsState as any,
      assignmentId,
      now
    )
  );

export const makeGetNumberOfAnswersNeedMarking: (
  AssignmentId: string,
  questionIds?: string[]
) => (state: State) => number = (assignmentId, questionIds) =>
  createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNumberOfAnswersNeedMarking(
      assignmentsState as any,
      assignmentId,
      questionIds
    )
  );

export const makeGetNumberOfAnswersNotRemarked: (
  AssignmentId: string,
  questionIds?: string[]
) => (state: State) => number = (assignmentId, questionIds) =>
  createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNumberOfAnswersNotRemarked(
      assignmentsState as any,
      assignmentId,
      questionIds
    )
  );

export const getNumberOfStudentsNeedMarking: (
  State: any,
  AssignmentId: string
) => number = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getNumberOfStudentsNeedMarking
);

export const {
  getAssignmentSectionAverageScore,
  getAssigneeStatus,
  getAssigneeAggregatedStats,
  getAssigneeTotalStudyTime,
  getAssigneeAverageScore,
  getAssigneeSectionStats,
  getAssigneeIndividualSectionStats,
  getNumberOfAssigneesWithStatus,
  getAssignees,
  getAssigneeSectionIds,
  isAssignmentWithStats,
  getIsAfterAssignmentDueDate,
  getSmartAssignmentSectionsFromConditions,
  getAssignee,
  getAssigneeQuestionStats,
  getAssigneeIndividualQuestionStats,
  getAssignmentQuestionsAverageMark,
  getAssignmentQuestionsMarksTotal,
  getAssignmentQuestionAverageMark,
  getAssignmentQuestionMarksTotal,
  getAssigneeQuestionTeacherMark,
  getAssigneeQuestionEmbeddedAnswerMarks,
  getAssigneeQuestionAutoMark,
  getAssigneeQuestionConfidence,
  getAssigneeQuestionTotalMarks,
  getAssigneeQuestionSelfMark,
  getAssigneeQuestionFlagged,
  getAssigneeQuestionNeedsMarking,
  getNumberOfAssigneesInProgress
} = composeSelectors(getStateAssignments as any, teacherAssignments as any);
